import { Button, Card, Flex, Form, TextInput } from "../../components";
import {
  COD_PAYMENT,
  PHONE_MESSAGE,
  PHONE_PATTERN,
  PICKUP_SLOTS,
} from "../../constants";
import {
  useApp,
  useAuth,
  useForm,
  useCheckoutGate,
  useNavigate,
} from "../../hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { AddAddress, CheckoutGateAlert, Summary } from "../../layouts";
import { useAppStore, useAuthStore, useCartStore } from "../../stores";
import { OrderType } from "../../types/enums";
import { Tools } from "../../utils";
import APIs from "../../apis";
import PaymentSelect from "./paymentSelect";

const Checkout = () => {
  const {
    items,
    checkout,
    promoCode,
    setPromoCode,
    checkoutPrefs,
    setCheckoutPrefs,
  } = useCartStore();
  const token = useAuthStore((state) => state.token);
  const { user, login } = useAuth();
  const { modal, location, message, payments, orderOptions } = useApp();
  const { blocked, blockReason } = useCheckoutGate();
  const { navigate } = useNavigate();
  const [promoInput, setPromoInput] = useState(promoCode || "");
  const payModalRef = useRef<{ destroy: () => void } | null>(null);
  const storedOrderType = useAppStore((state) => state.location?.orderType);
  const orderType: OrderType =
    location?.orderType ||
    storedOrderType ||
    (!(orderOptions?.allowDelivery ?? true)
      ? OrderType.pickup
      : OrderType.delivery);
  const accountPhone =
    user?.phone || (user as any)?.mobile || (user as any)?.mobileNumber;
  const availablePayments = useMemo(
    () =>
      (payments || []).filter(
        (method) => (method.enabled ?? true) && !method.comingSoon,
      ),
    [payments],
  );
  const infoForm = useForm({
    initialValues: {
      pickupSlotMinutes: 20,
      giftMessage: "",
      changeRequest: "",
      isGift: false,
      ...checkoutPrefs,
      paymentMethod: availablePayments[0]?.method || COD_PAYMENT.method,
      deliveryInfo: {
        ...checkoutPrefs.deliveryInfo,
        ...(!checkoutPrefs.deliveryInfo?.fullName && {
          fullName: user?.alias,
        }),
        ...(!checkoutPrefs.deliveryInfo?.phone && {
          phone: accountPhone,
        }),
      },
    },
    extras: [{ name: "paymentMethod" }],
  });
  const paymentMethod = Form.useWatch("paymentMethod", infoForm) || "cod";
  const isGift = Form.useWatch("isGift", infoForm);

  useEffect(() => {
    if (!items.length) navigate("/");
  }, []);

  const applyPromo = async () => {
    if (!promoInput) {
      setPromoCode(undefined);
      return;
    }
    try {
      const result = await APIs.app.verifyPromo(promoInput);
      setPromoCode(result.code || promoInput);
      message.success(`${result.percent}% off applied`);
    } catch (error: any) {
      setPromoCode(undefined);
      message.error(error.message || "Invalid promo code");
    }
  };

  const ensureLoggedIn = async (phone: string) => {
    if (token && (!accountPhone || Tools.samePhone(accountPhone, phone)))
      return;
    await login({ phone });
  };

  const validateCheckout = async () => {
    await infoForm.validateFields();
    const data = infoForm.getFieldsValue();
    if (orderType === OrderType.delivery && !location?.area?.uuid) {
      message.error("Please select a delivery area from the location menu");
      return;
    }
    if (orderType === OrderType.pickup && !location?.branch?.uuid) {
      message.error("Please select a pickup branch from the location menu");
      return;
    }
    if (blocked) {
      message.error(blockReason);
      return;
    }
    return data;
  };

  const openPaymentModal = () => {
    if (payModalRef.current) return;
    const payModal = modal.show({
      title: "Select payment",
      onClose: () => {
        payModalRef.current = null;
      },
      content: (
        <PaymentSelect
          infoForm={infoForm}
          availablePayments={availablePayments}
          onConfirm={() => submitOrder(payModal)}
        />
      ),
    });
    payModalRef.current = payModal;
  };

  const requestPayment = async () => {
    try {
      const data = await validateCheckout();
      if (!data) return;
      if (
        !availablePayments.some((method) => method.method === paymentMethod)
      ) {
        infoForm.setFieldValue(
          "paymentMethod",
          availablePayments[0]?.method || COD_PAYMENT.method,
        );
      }
      openPaymentModal();
    } catch (error: any) {
      if (!!error?.errorFields?.length)
        message.error(error.errorFields[0].errors[0]);
      else if (error?.message) message.error(error.message);
    }
  };

  const submitOrder = async (payModal?: { destroy: () => void }) => {
    try {
      const data = await validateCheckout();
      if (!data) return;
      payModal?.destroy();
      payModalRef.current = null;
      try {
        await ensureLoggedIn(data.deliveryInfo?.phone);
      } catch (error) {
        openPaymentModal();
        throw error;
      }
      const method =
        availablePayments.find((item) => item.method === data.paymentMethod)
          ?.method ||
        availablePayments[0]?.method ||
        COD_PAYMENT.method;
      const payload = {
        ...data,
        paymentMethod: method,
        orderType,
        areaUuid: location?.area?.uuid,
        branchUuid: location?.branch?.uuid ?? location?.area?.branchUuid,
        promoCode,
        idempotencyKey: Tools.uuid(),
      };
      setCheckoutPrefs(payload);
      const res = await checkout(payload);
      navigate(`/orders/${res.uuid}`);
      message.success(`Your Order #${res.orderNo} has been placed!`);
    } catch (error: any) {
      if (error?.code === "SITE_OFFLINE") {
        message.error(error.message || "We are temporarily closed.");
        return;
      }
      if (!!error?.errorFields?.length)
        message.error(error.errorFields[0].errors[0]);
      else if (error?.message) message.error(error.message);
    }
  };

  const phoneRule = {
    pattern: PHONE_PATTERN,
    message: PHONE_MESSAGE,
  };

  const selectAddress = () => {
    const picker = modal.show({
      title: "Select address",
      content: (
        <AddAddress
          onSelect={(address) => {
            infoForm.setFields([
              {
                name: ["deliveryInfo", "deliveryAddress"],
                value: (address.street || address.address || "").trim(),
              },
              {
                name: ["deliveryInfo", "nearestLandmark"],
                value: (address.nearest ?? "").trim(),
              },
            ]);
            picker.destroy();
          }}
        />
      ),
    });
  };

  return (
    <>
      <CheckoutGateAlert className="m-20-b" />
      <Flex gap={"2rem"} className="flex-column-mobile">
        <Flex flex={".6"} vertical gap={"2rem"}>
          <Card className="mobile:!rounded-none mobile:!border-0 mobile:!shadow-none mobile:[&_.ant-card-body]:!p-0">
            <Form
              form={infoForm}
              fields={[
                {
                  name: ["deliveryInfo", "fullName"],
                  label: "Full Name",
                  placeholder: "Enter your full name",
                  rules: [{ required: true }],
                },
                [
                  {
                    name: ["deliveryInfo", "phone"],
                    label: "Mobile Number",
                    placeholder: "03XXXXXXXXX",
                    rules: [{ required: true }, phoneRule],
                  },
                  {
                    name: ["deliveryInfo", "altPhone"],
                    label: "Alternate / WhatsApp (Optional)",
                    placeholder: "03XXXXXXXXX",
                    rules: [phoneRule],
                  },
                ],
                ...(orderType === OrderType.delivery
                  ? [
                      {
                        name: ["deliveryInfo", "deliveryAddress"],
                        label: `Address ( ${location?.area?.name}, ${location?.city?.name} ) `,
                        placeholder: "House / street",
                        rules: [
                          { required: true, message: "Please, enter address" },
                        ],
                        ...(!!user?.addresses?.length
                          ? {
                              addonAfter: (
                                <Button
                                  title="Select Address"
                                  className="!h-[3.2rem] !min-w-0 !px-[1.2rem]"
                                  onClick={selectAddress}
                                />
                              ),
                            }
                          : {}),
                      },
                      [
                        {
                          name: ["deliveryInfo", "nearestLandmark"],
                          label: "Landmark (Optional)",
                          placeholder: "Nearest landmark",
                        },
                        {
                          name: "email",
                          label: "Email (Optional)",
                          placeholder: "you@example.com",
                          rules: [{ type: "email" }],
                        },
                      ],
                    ]
                  : [
                      {
                        name: "email",
                        label: "Email (Optional)",
                        placeholder: "you@example.com",
                        rules: [{ type: "email" }],
                      },
                      {
                        name: "pickupSlotMinutes",
                        label: "Pickup time",
                        face: "select",
                        options: PICKUP_SLOTS.map((slot) => ({
                          label: `${slot} min`,
                          value: slot,
                        })),
                        rules: [{ required: true }],
                      },
                    ]),
                {
                  name: ["deliveryInfo", "notes"],
                  type: "textarea",
                  label: "Order notes (Optional)",
                  placeholder: "Any special instructions?",
                },
                {
                  name: "changeRequest",
                  label: "COD change request (Optional)",
                  placeholder: "e.g. Change for 1000",
                },
                {
                  name: "isGift",
                  face: "switch",
                  valuePropName: "checked",
                  label: "This is a gift",
                },
                ...(isGift
                  ? [
                      {
                        name: "giftMessage",
                        type: "textarea",
                        label: "Gift message",
                        placeholder: "Write a message",
                      },
                    ]
                  : []),
              ]}
            />
          </Card>
        </Flex>
        <Flex flex={".4"} align="flex-start">
          <Card className="w-100" classNames={{ body: "p-0" }}>
            <Summary items={items} branch={location?.branch}>
              <Flex gap={"1rem"} className="m-20-t" align="center">
                <TextInput
                  className="flex-1 [&_.group]:flex [&_.group]:h-[4rem] [&_.group]:items-center [&_.group]:!py-0"
                  value={promoInput}
                  placeholder="Promo code"
                  onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                />
                <Button title="Apply" onClick={applyPromo} />
              </Flex>
              {!blocked && (
                <Button
                  color="base"
                  title="Place Order"
                  className="m-20-t w-100"
                  onClick={requestPayment}
                />
              )}
            </Summary>
          </Card>
        </Flex>
      </Flex>
    </>
  );
};

export default Checkout;
